import { useCallback, useEffect, useMemo } from 'react';

import type { DocumentComment, DocumentCommentsById, UUID } from '@aline/common';
import { AlineTransforms, Queries } from '@aline/common';
import { link, params } from '@aline/routes';
import type { ApolloCache, ApolloClient, ApolloLink, Reference } from '@apollo/client';
import { gql, makeVar } from '@apollo/client';
import { useApolloClient, useReactiveVar } from '@apollo/client/react';
import { uniqWith } from 'ramda';
import { useNavigate } from 'react-router-dom';
import { Editor, Point, Text } from 'slate';
import type { OnSubmitCommentOptions, OnSubmitCommentReturn } from 'src/components';
import {
  useEditorTabStateSelector,
  useEditorTabStateSelectorForTabId,
  useGetCommentIds,
} from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  commentTypename,
  taskTypename,
  useConsumePendingParam,
  useDocumentParams,
} from 'src/helpers';
import { ReactEditor, useEditor, useEditorLoading, useMaybeEditor } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

export function getSortCommentsComparator(
  editor: Editor
): (
  a: Pick<Apollo.Comment, 'nodeRefId' | '__typename'>,
  b: Pick<Apollo.Comment, 'nodeRefId' | '__typename'>
) => number {
  return (commentA, commentB) => {
    if (!commentA.nodeRefId || !commentB.nodeRefId) return 0;

    const commentAEntries = Editor.nodes(editor, {
      at: [],
      match: node =>
        Text.isText(node) &&
        Array.isArray(node.comments) &&
        node.comments.some(id => id === commentA.nodeRefId),
    });
    const commentBEntries = Editor.nodes(editor, {
      at: [],
      match: node =>
        Text.isText(node) &&
        Array.isArray(node.comments) &&
        node.comments.some(id => id === commentB.nodeRefId),
    });

    const firstCommentA = commentAEntries.next().value;
    const firstCommentB = commentBEntries.next().value;
    if (!firstCommentA || !firstCommentB) return 0;

    const [, commentAFirstPath] = firstCommentA;
    const [, commentBFirstPath] = firstCommentB;

    const startAtA = Editor.start(editor, commentAFirstPath);
    const startAtB = Editor.start(editor, commentBFirstPath);

    if (Point.isBefore(startAtA, startAtB)) return -1;
    if (Point.isAfter(startAtA, startAtB)) return 1;
    if (Point.equals(startAtA, startAtB)) return 0;

    return 0;
  };
}

export function useSortCommentsByDocPosition<
  T extends Pick<Apollo.Comment, 'nodeRefId' | '__typename'>,
>(comments: T[]): { editor: Editor | null; sortedComments: T[] } {
  const editor = useMaybeEditor();
  const sortedComments = useMemo(() => {
    if (!editor) return comments;
    return [...comments].sort(getSortCommentsComparator(editor));
  }, [comments, editor]);

  return { editor, sortedComments };
}

type CommentEntityTypename =
  | Apollo.Document['__typename']
  | Apollo.Task['__typename']
  | Apollo.Workflow['__typename'];

/**
 * Delete a comment.
 */
export function useDeleteComment(): {
  deleteComment: (
    commentId: UUID,
    commentableId: UUID,
    __typename: CommentEntityTypename,
    options: Parameters<Apollo.DeleteCommentMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DeleteCommentMutation>>;
} {
  const [deleteComment] = Apollo.useDeleteCommentMutation();

  return {
    deleteComment: (commentId, commentableId, __typename, options) => {
      return deleteComment({
        ...options,
        update: cache => {
          cache.modify({
            fields: {
              comments: (existingCommentRefs: readonly Reference[] = [], { readField }) => {
                return existingCommentRefs.filter(
                  (ref: Reference) => readField('id', ref) !== commentId
                );
              },
            },
            id: cache.identify({ __typename, id: commentableId }),
          });
        },
      });
    },
  };
}

/**
 * Create a comment.
 */
export function useCreateComment(): {
  createComment: (
    commentableId: UUID,
    __typename: CommentEntityTypename,
    options: Parameters<Apollo.CreateCommentMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateCommentMutation>>;
  loading: boolean;
} {
  const [createComment, { loading }] = Apollo.useCreateCommentMutation();
  const safeCreateComment = useCallback(
    (
      commentableId: UUID,
      __typename: CommentEntityTypename,
      options: Parameters<Apollo.CreateCommentMutationHookResult[0]>[0]
    ) => {
      return createComment({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              comments: (existingCommentRefs, { readField }) => {
                const newCommentRef = cache.writeFragment({
                  data: data?.createComment.comment,
                  fragment: gql`
                    fragment NewComment on Comment {
                      __typename
                      id
                    }
                  `,
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...existingCommentRefs, newCommentRef]
                );
              },
            },
            id: cache.identify({ __typename, id: commentableId }),
          });
        },
      });
    },
    [createComment]
  );

  return {
    createComment: safeCreateComment,
    loading,
  };
}

export function useCreateTaskActivityComment(taskId: UUID) {
  const currentUserId = useCurrentUserId();
  const { createComment, loading } = useCreateComment();

  const safeCreateTaskActivityComment = useCallback(
    async (text: string, options: OnSubmitCommentOptions = {}): Promise<OnSubmitCommentReturn> => {
      if (!currentUserId) return null;

      const { data } = await createComment(taskId, taskTypename, {
        variables: {
          input: {
            commentProps: {
              audience: options.audience ?? null,
              authorId: currentUserId,
              commentableId: taskId,
              commentableType: Apollo.CommentableType.Task,
              nodeRefId: null,
              parentCommentId: null,
              text,
            },
          },
        },
      });

      return data?.createComment.comment ?? null;
    },
    [createComment, currentUserId, taskId]
  );

  return { createTaskActivityComment: safeCreateTaskActivityComment, loading };
}

export async function refetchDocumentCommentQueries(
  client: ApolloClient,
  documentId: UUID
): Promise<void> {
  const { data } = await client.query<Apollo.GetDocumentCommentsQuery>({
    fetchPolicy: 'network-only',
    query: Apollo.GetDocumentCommentsDocument,
    variables: { documentId },
  });
  const documentRootCommentIds = new Set(
    (data?.document?.comments ?? []).map(comment => comment.id)
  );

  await client.refetchQueries({
    include: 'active',
    onQueryUpdated(observableQuery) {
      if (observableQuery.queryName !== Apollo.namedOperations.Query.GetCommentReplies)
        return false;

      const rootCommentId = observableQuery.variables?.where?.id;
      return typeof rootCommentId === 'string' && documentRootCommentIds.has(rootCommentId);
    },
  });
}

export function applyThreadIsPrivateToCache(
  cache: ApolloCache,
  rootCommentId: UUID,
  isPrivate: boolean
): void {
  const rootCacheId = cache.identify({ __typename: commentTypename, id: rootCommentId });
  if (!rootCacheId) return;

  // GraphQL resolves effective isPrivate on replies too — keep reply cache entries in sync.
  cache.modify({
    fields: {
      isPrivate: () => isPrivate,
      replies(existingReplyRefs: readonly Reference[] = [], { readField }) {
        for (const ref of existingReplyRefs) {
          const replyId = readField<string>('id', ref);
          const replyCacheId = replyId
            ? cache.identify({ __typename: commentTypename, id: replyId })
            : null;
          if (!replyCacheId) continue;

          cache.modify({
            fields: { isPrivate: () => isPrivate },
            id: replyCacheId,
          });
        }

        return existingReplyRefs;
      },
    },
    id: rootCacheId,
  });
}

export function useUpdateCommentThreadPrivacy(rootCommentId: UUID): {
  updateThreadPrivacy: (isPrivate: boolean) => Promise<void>;
} {
  const client = useApolloClient();
  const [updateComment] = Apollo.useUpdateCommentMutation();

  const updateThreadPrivacy = useCallback(
    async (isPrivate: boolean) => {
      const { data } = await updateComment({
        variables: { input: { id: rootCommentId, props: { isPrivate } } },
      });
      if (!data?.updateComment?.comment) return;

      applyThreadIsPrivateToCache(client.cache, rootCommentId, isPrivate);
    },
    [client.cache, rootCommentId, updateComment]
  );

  return { updateThreadPrivacy };
}

/**
 * Creates a reply to a comment.
 */
export function useCreateCommentReply(): {
  createCommentReply: (
    commentId: UUID,
    options: Parameters<Apollo.CreateCommentMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateCommentMutation>>;
  loading: boolean;
} {
  const [createComment, { loading }] = Apollo.useCreateCommentMutation();

  return {
    createCommentReply: (commentId, options) => {
      return createComment({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              replies: (existingReplyRefs: readonly Reference[] = [], { readField }) => {
                const reply = data?.createComment.comment;
                if (!reply) return existingReplyRefs;

                const newReplyRef = cache.writeFragment({
                  data: reply,
                  fragment: gql`
                    fragment NewReply on Comment {
                      __typename
                      id
                    }
                  `,
                });
                if (!newReplyRef) return existingReplyRefs;

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...existingReplyRefs, newReplyRef]
                );
              },
            },
            id: cache.identify({ __typename: commentTypename, id: commentId }),
          });
        },
      });
    },
    loading,
  };
}

/**
 * Deletes a reply to a comment.
 */
export function useDeleteCommentReply(): {
  deleteCommentReply: (
    commentId: UUID,
    replyId: UUID,
    options: Parameters<Apollo.DeleteCommentMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DeleteCommentMutation>>;
} {
  const [deleteComment] = Apollo.useDeleteCommentMutation();

  return {
    deleteCommentReply: (commentId, replyId, options) => {
      return deleteComment({
        ...options,
        update: cache => {
          cache.modify({
            fields: {
              replies: (existingReplyRefs: readonly Reference[] = [], { readField }) => {
                return existingReplyRefs.filter(
                  (ref: Reference) => readField('id', ref) !== replyId
                );
              },
            },
            id: cache.identify({ __typename: commentTypename, id: commentId }),
          });
        },
      });
    },
  };
}

/**
 * Get comments that appear in the JSON of a given Document.
 */
export function useGetDocumentComments(
  documentId: UUID,
  options?: Parameters<typeof Apollo.useGetDocumentCommentsQuery>[0]
): {
  allComments: DocumentComment[];
  commentsById: DocumentCommentsById;
  commentsInEditor: DocumentComment[];
  loading: boolean;
} {
  const uniqueCommentIds = useGetCommentIds();
  const { data, loading } = Apollo.useGetDocumentCommentsQuery({
    ...options,
    variables: { documentId },
  });
  const allComments = useMemo(() => data?.document.comments ?? [], [data?.document.comments]);

  /**
   * Comments without a nodeRefId are *not* attached to text nodes and are simply top-level comments on the Document.
   */
  const commentsInEditor = useMemo(() => {
    return allComments.filter(c => !!c.nodeRefId && uniqueCommentIds.includes(c.nodeRefId));
  }, [allComments, uniqueCommentIds]);

  const commentsById: DocumentCommentsById = useMemo(
    () => new Map(commentsInEditor.flatMap(c => (c.nodeRefId ? [[c.nodeRefId, c]] : []))),
    [commentsInEditor]
  );

  return { allComments, commentsById, commentsInEditor, loading };
}

export const activeCommentIdVar = makeVar<UUID | null>(null);
export function useActiveCommentId(): UUID | null {
  return useReactiveVar(activeCommentIdVar);
}

/**
 * Sets the activeCommentId for the Editor.
 */
export function useSetActiveComment(editor: Editor, tabId: UUID): void {
  const { documentId } = useDocumentParams();
  const { commentsById } = useGetDocumentComments(documentId);
  const selectedCommentIds = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.comments.selectedCommentIds
  );
  const unresolvedSelectedCommentIds = useMemo(
    () => selectedCommentIds.filter(commentId => !commentsById.get(commentId)?.resolved),
    [selectedCommentIds, commentsById]
  );

  useEffect(() => {
    const activeCommentId = Queries.getShortestCommentId(editor, {
      commentIds: unresolvedSelectedCommentIds,
    });

    activeCommentIdVar(activeCommentId);
  }, [commentsById.size, selectedCommentIds, tabId, unresolvedSelectedCommentIds, editor]);
}

/**
 * Scroll the Editor to where the commentId is from a one-shot deep link, if any.
 */
export function useScrollToURLDocComment(): void {
  const editor = useEditor();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const editorLoading = useEditorLoading();
  const [commentIdArrival, acknowledgeArrival] = useConsumePendingParam(params.commentId);

  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  useEffect(() => {
    if (editorLoading || !rWHeightPx || !commentIdArrival) return;

    acknowledgeArrival();
    // Wrapping in setTimeout improves scrolling to comment UX.
    setTimeout(() => {
      AlineTransforms.selectComment(editor, {
        commentId: commentIdArrival.value,
        select: 'start',
      });
      ReactEditor.blur(editor);
      navigate(link.DocumentDetailComments({ documentId }), { replace: true });
      openDrawer();
    }, 1000);
  }, [
    acknowledgeArrival,
    commentIdArrival,
    documentId,
    editor,
    editorLoading,
    navigate,
    openDrawer,
    rWHeightPx,
    rWOffsetPx,
  ]);
}
